import { useState, useEffect } from "react";
import { supabase, type Post } from "@/lib/supabase";
import ImageUploadField from "@/components/base/ImageUploadField";

export default function PostsManager() {
  const [postList, setPostList] = useState<Post[]>([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image: "",
    external_url: "",
    event_date: "",
    category: "news" as "news" | "event" | "announcement",
    status: "draft" as "draft" | "published",
  });

  const emptyForm = {
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image: "",
    external_url: "",
    event_date: "",
    category: "news" as "news" | "event" | "announcement",
    status: "draft" as "draft" | "published",
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setIsLoading(true);
    const { data } = await supabase
      .from("posts")
      .select("*")
      .order("created_at", { ascending: false });
    setPostList(data || []);
    setIsLoading(false);
  };

  const filtered = postList.filter(
    (p) =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.category.toLowerCase().includes(search.toLowerCase())
  );

  const handleEdit = (post: Post) => {
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      featured_image: post.featured_image,
      external_url: post.external_url || "",
      event_date: post.event_date || "",
      category: post.category,
      status: post.status,
    });
    setEditingId(post.id);
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...formData,
      external_url: formData.external_url.trim() || null,
      event_date: formData.event_date.trim() || null,
    };

    if (editingId) {
      const { error } = await supabase
        .from("posts")
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq("id", editingId);
      if (!error) await fetchPosts();
    } else {
      const { data: userData } = await supabase.auth.getUser();
      const { error } = await supabase.from("posts").insert({
        ...payload,
        created_by: userData.user?.id || null,
      });
      if (!error) await fetchPosts();
    }

    setSaving(false);
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this post?")) return;
    const { error } = await supabase.from("posts").delete().eq("id", id);
    if (!error) await fetchPosts();
  };

  const handleClose = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(emptyForm);
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-10 bg-gray-100 rounded w-full max-w-md" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl text-primary-900">Posts Management</h1>
        <button
          onClick={handleNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" /> New Post
        </button>
      </div>

      <div className="mb-4">
        <div className="relative max-w-md">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Post</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Category</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">External Link</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-4 md:px-6 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50/50">
                  <td className="px-4 md:px-6 py-3">
                    <div className="flex items-center gap-3">
                      {post.featured_image && (
                        <img
                          src={post.featured_image}
                          alt=""
                          className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover hidden sm:block"
                        />
                      )}
                      <div>
                        <p className="font-medium text-sm text-gray-800 line-clamp-1">{post.title}</p>
                        <p className="text-xs text-gray-400 line-clamp-1 max-w-[200px]">{post.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 md:px-6 py-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${post.category === "event" ? "bg-gold-500" : post.category === "news" ? "bg-primary-600" : "bg-emerald-600"}`}>
                      {post.category}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${post.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-yellow-50 text-yellow-700"}`}>
                      {post.status}
                    </span>
                  </td>
                  <td className="px-4 md:px-6 py-3">
                    {post.external_url ? (
                      <a
                        href={post.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-gold-600 hover:text-gold-700 font-medium"
                      >
                        <i className="ri-external-link-line" /> Link
                      </a>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 md:px-6 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(post.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </td>
                  <td className="px-4 md:px-6 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(post)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary-600 transition-colors"
                        aria-label="Edit"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors"
                        aria-label="Delete"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">No posts found matching your search.</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-heading font-bold text-lg text-primary-900">
                {editingId ? "Edit Post" : "New Post"}
              </h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Slug</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as "news" | "event" | "announcement" })}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                  >
                    <option value="news">News</option>
                    <option value="event">Event</option>
                    <option value="announcement">Announcement</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as "draft" | "published" })}
                    className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Content</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={6}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none"
                />
              </div>

              <ImageUploadField
                label="Cover Image"
                value={formData.featured_image}
                onChange={(url) => setFormData({ ...formData, featured_image: url })}
                folder="posts"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Event Date <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <i className="ri-calendar-event-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">
                    Displayed as the actual event date on cards and detail pages.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    External Link <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="relative">
                    <i className="ri-links-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="url"
                      value={formData.external_url}
                      onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                      placeholder="https://facebook.com/events/..."
                      className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-400">
                    If set, clicking opens the external link instead.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={handleClose}
                className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {saving ? "Saving..." : "Save Post"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}